import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchGradesStart, fetchGradesSuccess, fetchGradesFailure } from '../../../redux/slices/gradesSlice';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getGrades } from '../../../Action/api';

const Grades = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  
  const { data: grades, loading, error } = useSelector((state) => state.grades);
  
  const [filteredGrades, setFilteredGrades] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    if (grades.length === 0) {
      fetchGrades();
    } else {
      setFilteredGrades(grades);
    }
  }, []);

  useEffect(() => {
    setFilteredGrades(grades);
  }, [grades]);

  const fetchGrades = async () => {
    try {
      dispatch(fetchGradesStart());
      const data = await getGrades();
      dispatch(fetchGradesSuccess(data));
    } catch (err) {
      dispatch(fetchGradesFailure('Failed to fetch grades. Please try again.'));
      console.error('Error fetching grades:', err);
    }
  };

  useEffect(() => {
    const filtered = grades.filter((grade) =>
      grade.gradeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      grade.gradeId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      grade.expRange.toLowerCase().includes(searchTerm.toLowerCase()) ||
      grade.salRange.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredGrades(filtered);
    setPage(0);
  }, [searchTerm, grades]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedGrades = filteredGrades.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 2 }, 
      maxWidth: { xs: '100vw', md: 'calc(100vw - 280px)' }, 
      mx: 'auto',
      width: '100%'
    }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' }, 
        mb: 2,
        gap: { xs: 1, sm: 0 }
      }}>
        <Typography 
          variant={isMobile ? 'h6' : 'h5'} 
          component="h1" 
          sx={{ fontWeight: 600 }}
        >
          Grades Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />}
            size="small"
            onClick={fetchGrades}
            disabled={loading}
            sx={{ textTransform: 'none' }}
          >
            {!isMobile && 'Reload'}
          </Button>
          <Button 
            variant="contained" 
            startIcon={!isMobile && <AddIcon />}
            size="small"
            fullWidth={isMobile}
            sx={{ textTransform: 'none' }}
          >
            Add Grade
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by grade name, code, experience, or salary range..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      ) : (
        <>
          <TableContainer 
            component={Paper} 
            sx={{ 
              boxShadow: 2,
              overflowX: 'auto',
              width: '100%',
              '&::-webkit-scrollbar': {
                height: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#888',
                borderRadius: '4px',
              }
            }}
          >
            <Table size="small" sx={{ minWidth: { xs: 600, sm: 750 } }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Grade ID</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Grade Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Experience Range</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Salary Range</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedGrades.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        No grades found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedGrades.map((grade, index) => (
                    <TableRow 
                      key={grade.id}
                      sx={{ 
                        '&:hover': { backgroundColor: '#f9f9f9' },
                        '&:nth-of-type(even)': { backgroundColor: '#fafafa' }
                      }}
                    >
                      <TableCell sx={{ fontSize: '0.813rem' }}>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell sx={{ fontSize: '0.813rem' }}>
                        <Chip label={grade.gradeId} size="small" variant="outlined" color="primary" />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.813rem', fontWeight: 500 }}>{grade.gradeName}</TableCell>
                      <TableCell sx={{ fontSize: '0.813rem' }}>{grade.expRange}</TableCell>
                      <TableCell sx={{ fontSize: '0.813rem' }}>
                        <Chip label={grade.salRange} size="small" color="success" variant="outlined" />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" color="primary">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component={Paper}
            sx={{ 
              mt: 1, 
              boxShadow: 1,
              '.MuiTablePagination-toolbar': {
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                justifyContent: 'center'
              },
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            count={filteredGrades.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={isMobile ? 'Rows:' : 'Rows per page:'}
          />
        </>
      )}
    </Box>
  );
};

export default Grades;
